import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useAuthInfo } from "@propelauth/react";
import {
  getInputDocumentPageSummaries,
  getCase,
  getInputDocumentPageSegmentations,
  downloadInputDocument,
} from "@/utils/apiCalls";
import {
  Case,
  DocumentPageSegmentation,
  InputDocumentPageSummary,
  PageSummariesResponse,
} from "@/types";
import { Button } from "@/components/ui/button";
import { Layout } from "@/components/Layout";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import PdfViewer, { PdfViewerRefProps } from "@/components/pdfViewer/PDFViewer";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { MultiSelectControl } from "@/components/ui/MultiSelectControl";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { LoadingView } from "@/components/Loader";

const BreadcrumbNav = (props: {
  caseId: string;
  caseName: string;
  documentName: string;
  documentId: string;
}) => {
  return (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink href="/case/all">Cases</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator />
        <BreadcrumbItem>
          <BreadcrumbLink href={`/case/${props.caseId}`}>
            {props.caseName}
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator />
        <BreadcrumbItem>
          <BreadcrumbLink
            href={`/case/${props.caseId}/output-document/${props.documentId}`}
          >
            Document
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator />
        <BreadcrumbItem>
          <BreadcrumbPage>Page Summaries: {props.documentName}</BreadcrumbPage>
        </BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>
  );
};

const SummaryPageItem = ({
  summary,
  onClick,
  isActive,
  segmentations,
}: {
  summary: InputDocumentPageSummary;
  onClick: (pageNumber: number) => void;
  isActive: boolean;
  segmentations: DocumentPageSegmentation[];
}) => {
  // The page in the summary is 0-indexed, and the segmentations also use 0-indexed pages
  const pageNumber = summary.page;
  const relevantSegmentation = segmentations.find(
    (s) => pageNumber >= s.start_page && pageNumber <= s.end_page
  );

  return (
    <div
      key={summary.id}
      className={cn(
        "border-t py-4 px-4 cursor-pointer hover:bg-gray-50",
        isActive && "bg-gray-50"
      )}
      onClick={() => onClick(pageNumber + 1)} // Add 1 here for display purposes only
    >
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-lg font-semibold w-fit">
          Page {pageNumber + 1} {/* Add 1 here for display purposes only */}
        </h2>
        <div className="flex items-center gap-2">
          {relevantSegmentation && (
            <Badge variant="outline" className="text-xs">
              {relevantSegmentation.name} - {relevantSegmentation.date}
            </Badge>
          )}
          {summary.clinical_info && (
            <Badge
              variant="default"
              className="bg-blue-500 hover:bg-blue-600 text-xs"
            >
              Clinical Info
            </Badge>
          )}
          {summary.medication_info && (
            <Badge
              variant="default"
              className="bg-green-500 hover:bg-green-600 text-xs"
            >
              Medication Info
            </Badge>
          )}
          {summary.previous_evaluation && (
            <Badge
              variant="default"
              className="bg-purple-500 hover:bg-purple-600 text-xs"
            >
              Previous Evaluation
            </Badge>
          )}
        </div>
      </div>
      <div className="whitespace-pre-wrap text-gray-700">{summary.summary}</div>
    </div>
  );
};

export const DocumentPageSummariesPage = () => {
  const { caseId, outputDocumentId, inputDocumentId } = useParams<{
    caseId: string;
    outputDocumentId: string;
    inputDocumentId: string;
  }>();
  const authInfo = useAuthInfo();

  const [summariesLoading, setSummariesLoading] = useState(false);
  const [documentLoading, setDocumentLoading] = useState(false);
  const [case_, setCase] = useState<Case | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [summariesData, setSummariesData] =
    useState<PageSummariesResponse | null>(null);
  const [documentUrl, setDocumentUrl] = useState<string>("");
  const [activePage, setActivePage] = useState<number>(1);
  const [showDocument, setShowDocument] = useState(false);
  const [segmentations, setSegmentations] = useState<
    DocumentPageSegmentation[]
  >([]);
  const [selectedSegmentation, setSelectedSegmentation] =
    useState<DocumentPageSegmentation | null>(null);
  const [clinicalInfoFilter, setClinicalInfoFilter] = useState<
    boolean | undefined
  >(undefined);
  const [medicationInfoFilter, setMedicationInfoFilter] = useState<
    boolean | undefined
  >(undefined);
  const [previousEvaluationFilter, setPreviousEvaluationFilter] = useState<
    boolean | undefined
  >(undefined);

  const pdfViewerRef = useRef<PdfViewerRefProps>(null);

  useEffect(() => {
    fetchCase();
    fetchSegmentations();
    fetchDocument();
  }, [caseId]);

  useEffect(() => {
    fetchSummaries();
  }, [
    inputDocumentId,
    currentPage,
    pageSize,
    clinicalInfoFilter,
    medicationInfoFilter,
    previousEvaluationFilter,
    selectedSegmentation?.id,
  ]);

  const fetchDocument = async () => {
    if (!inputDocumentId) return;
    setDocumentLoading(true);
    const documentResponse = await downloadInputDocument(
      inputDocumentId,
      authInfo.accessToken ?? null,
      true
    );
    setDocumentLoading(false);
    if (documentResponse !== null) {
      setDocumentUrl(documentResponse);
    } else {
      toast.error("Failed to load document");
    }
  };

  const fetchSummaries = async () => {
    if (!inputDocumentId) return;
    setSummariesLoading(true);

    const summariesResponse = await getInputDocumentPageSummaries(
      inputDocumentId,
      currentPage,
      pageSize,
      authInfo.accessToken ?? null,
      selectedSegmentation?.id,
      clinicalInfoFilter,
      medicationInfoFilter,
      previousEvaluationFilter
    );

    setSummariesLoading(false);
    if (summariesResponse !== null) {
      setSummariesData(summariesResponse);
    } else {
      toast.error("Failed to load document page summaries");
    }
  };

  const fetchCase = async () => {
    if (!caseId) return;
    const caseResponse = await getCase(caseId, authInfo.accessToken ?? null);
    if (caseResponse !== null) {
      setCase(caseResponse);
    } else {
      toast.error("Failed to load case");
    }
  };

  const fetchSegmentations = async () => {
    if (!inputDocumentId) return;
    const segmentationsResponse = await getInputDocumentPageSegmentations(
      inputDocumentId,
      authInfo.accessToken ?? null
    );
    if (segmentationsResponse !== null) {
      setSegmentations(segmentationsResponse);
    } else {
      toast.error("Failed to load document segmentations");
    }
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newSize: string) => {
    setPageSize(Number(newSize));
    setCurrentPage(1);
  };

  const handleSummaryClick = (pageNumber: number) => {
    setActivePage(pageNumber);
    setShowDocument(true);
    pdfViewerRef.current?.goToPage(pageNumber);
  };

  const toggleDocument = () => {
    setShowDocument((oldVal) => !oldVal);
  };

  const handleSegmentationSelect = (
    item: { id: string; name: string },
    isSelected: boolean
  ) => {
    if (isSelected) {
      const selected = segmentations.find((s) => s.id === item.id);
      if (selected) {
        setSelectedSegmentation(selected);
        setCurrentPage(1); // Reset to first page when changing segmentation
      }
    } else {
      setSelectedSegmentation(null);
      setCurrentPage(1); // Reset to first page when clearing segmentation
    }
  };

  const clearSelectedSegmentation = () => {
    setSelectedSegmentation(null);
    setCurrentPage(1); // Reset to first page when clearing segmentation
  };

  const handleClinicalInfoFilterChange = (checked: boolean) => {
    setClinicalInfoFilter(checked ? true : undefined);
    setCurrentPage(1); // Reset to first page when changing filter
  };

  const handleMedicationInfoFilterChange = (checked: boolean) => {
    setMedicationInfoFilter(checked ? true : undefined);
    setCurrentPage(1); // Reset to first page when changing filter
  };

  const handlePreviousEvaluationFilterChange = (checked: boolean) => {
    setPreviousEvaluationFilter(checked ? true : undefined);
    setCurrentPage(1); // Reset to first page when changing filter
  };

  return (
    <Layout title="Document Page Summaries">
      <div className="space-y-4">
        <BreadcrumbNav
          caseId={caseId!}
          caseName={case_?.clinician.name ?? ""}
          documentName={
            case_?.output_documents.find((doc) => doc.id === outputDocumentId)
              ?.document_type_name ?? ""
          }
          documentId={outputDocumentId!}
        />
        <div
          className={cn(
            "flex items-center flex-wrap justify-between space-x-4 transition-all duration-300 ease-in-out",
            showDocument ? "mr-[612px]" : "mr-0"
          )}
        >
          <div className="flex items-center space-x-2">
            {segmentations.length > 0 && (
              <MultiSelectControl
                title="Filter by Document"
                items={segmentations.map((s) => ({
                  id: s.id,
                  name: `${s.name} - ${s.date} - ${s.author} (Pages ${s.start_page}-${s.end_page})`,
                }))}
                selectedItems={
                  selectedSegmentation
                    ? [
                        {
                          id: selectedSegmentation.id,
                          name: `${selectedSegmentation.name} - ${selectedSegmentation.date} - ${selectedSegmentation.author} (Pages ${selectedSegmentation.start_page}-${selectedSegmentation.end_page})`,
                        },
                      ]
                    : []
                }
                selectItem={handleSegmentationSelect}
                clearSelectedItems={clearSelectedSegmentation}
                limitOne={true}
              />
            )}

            <div className="flex items-center space-x-4 ml-4">
              <div className="flex items-center space-x-2">
                <Switch
                  id="clinical-info-filter"
                  checked={clinicalInfoFilter === true}
                  onCheckedChange={handleClinicalInfoFilterChange}
                />
                <Label htmlFor="clinical-info-filter">Clinical Info</Label>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="medication-info-filter"
                  checked={medicationInfoFilter === true}
                  onCheckedChange={handleMedicationInfoFilterChange}
                />
                <Label htmlFor="medication-info-filter">Medication Info</Label>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="previous-evaluation-filter"
                  checked={previousEvaluationFilter === true}
                  onCheckedChange={handlePreviousEvaluationFilterChange}
                />
                <Label htmlFor="previous-evaluation-filter">
                  Previous Evaluation
                </Label>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <span className="text-sm">Show</span>
            <Select
              value={pageSize.toString()}
              onValueChange={handlePageSizeChange}
            >
              <SelectTrigger className="w-20">
                <SelectValue placeholder={pageSize.toString()} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-sm">entries</span>
          </div>
          {summariesData && summariesData.total > pageSize && (
            <div className="flex items-center space-x-2">
              <div className="text-xs text-muted-foreground">
                Page {currentPage} of{" "}
                {Math.ceil(summariesData.total / pageSize)}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  handlePageChange(
                    Math.min(
                      Math.ceil(summariesData.total / pageSize),
                      currentPage + 1
                    )
                  )
                }
                disabled={
                  currentPage >= Math.ceil(summariesData.total / pageSize)
                }
              >
                Next
              </Button>
            </div>
          )}
        </div>
        {summariesLoading ? (
          <LoadingView text="Loading page summaries..." />
        ) : summariesData && summariesData.summaries.length > 0 ? (
          <div className="relative flex">
            <div
              className={cn(
                "w-full transition-all duration-300 ease-in-out",
                showDocument ? "mr-[612px]" : "mr-0"
              )}
            >
              {summariesData.summaries.map((summary) => (
                <SummaryPageItem
                  key={summary.id}
                  summary={summary}
                  onClick={handleSummaryClick}
                  isActive={activePage === summary.page + 1}
                  segmentations={segmentations}
                />
              ))}
              <div className="border-t"></div>
            </div>
            {activePage &&
              (documentLoading ? (
                <LoadingView text="Loading document..." />
              ) : (
                <div
                  className={cn(
                    "fixed top-0 right-0 h-screen w-[612px] transition-transform duration-300 ease-in-out bg-white shadow-lg",
                    showDocument ? "translate-x-0" : "translate-x-full"
                  )}
                >
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute -left-10 top-4 z-10"
                    onClick={toggleDocument}
                  >
                    {showDocument ? <ChevronRight /> : <ChevronLeft />}
                  </Button>
                  <div className="flex items-center justify-center h-full pt-4">
                    <PdfViewer
                      ref={pdfViewerRef}
                      fileUrl={documentUrl}
                      defaultPage={activePage}
                    />
                  </div>
                </div>
              ))}
          </div>
        ) : (
          <div className="py-6 px-4 text-center border-t border-b">
            <p>
              No page summaries available for this document with the selected
              filters.
            </p>
          </div>
        )}
      </div>
    </Layout>
  );
};
